import type { PreviewSupport } from '@/app/lib/quant/types'

const FIXED_CHOICES = [500, 2000, 5000]
const DEFAULT_BAR_COUNT = 2000

/** Timeframes the preview offers, in the server's order. */
export function timeframesOf(preview: PreviewSupport): string[] {
  return [...preview.timeframes]
}

function validCap(cap: unknown): cap is number {
  return typeof cap === 'number' && Number.isInteger(cap) && cap > 0
}

/** Bar-count choices for a timeframe: 500, 2,000, 5,000 up to the cap, then the cap itself. `null` when no valid cap is declared. */
export function barChoices(tf: string, maxBars: Record<string, number>): number[] | null {
  const cap = maxBars[tf]
  if (!validCap(cap)) return null
  const choices = FIXED_CHOICES.filter((n) => n <= cap)
  if (!choices.includes(cap)) choices.push(cap)
  return choices
}

/** 2,000 bars, or the cap when that is smaller. */
export function defaultBars(cap: number): number {
  return Math.min(DEFAULT_BAR_COUNT, cap)
}

/** Keep a bar count within 1..cap. */
export function clampBars(bars: number, cap: number): number {
  return Math.max(1, Math.min(bars, cap))
}

/** The persisted timeframe if offered, else `5m` if offered, else the first offered. */
export function defaultTf(persisted: string | null, preview: PreviewSupport): string {
  if (persisted !== null && preview.timeframes.includes(persisted)) return persisted
  if (preview.timeframes.includes('5m')) return '5m'
  return preview.timeframes[0] ?? ''
}

/** Why the preview cannot run for this timeframe, or `null` when it can. */
export function previewUnavailableReason(preview: PreviewSupport, tf: string): string | null {
  return barChoices(tf, preview.max_bars) === null ? `The preview did not declare a bar limit for ${tf}.` : null
}
